import React, { useState, useEffect, useCallback } from "react";
import { RefreshCw, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const PWA_ICON_VERSION = 2;
const VERSION_KEY = "pwa-icon-version-acked";
const SNOOZE_KEY = "pwa-reinstall-snoozed-at";
const SNOOZE_HOURS = 24;

function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as any).standalone === true
  );
}

function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
}

function shouldShow(): boolean {
  if (!isStandalone()) return false;

  const raw = localStorage.getItem(VERSION_KEY);

  // First time in standalone = fresh install → mark as current, don't show
  if (raw === null) {
    localStorage.setItem(VERSION_KEY, String(PWA_ICON_VERSION));
    return false;
  }

  const acked = Number(raw);
  if (acked >= PWA_ICON_VERSION) return false;

  const snoozed = Number(localStorage.getItem(SNOOZE_KEY) || "0");
  if (snoozed && (Date.now() - snoozed) / (1000 * 60 * 60) < SNOOZE_HOURS) return false;

  return true;
}

const PWAReinstallBanner: React.FC = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(shouldShow());
  }, []);

  const handleDone = useCallback(() => {
    localStorage.setItem(VERSION_KEY, String(PWA_ICON_VERSION));
    localStorage.removeItem(SNOOZE_KEY);
    setVisible(false);
  }, []);

  const handleSnooze = useCallback(() => {
    localStorage.setItem(SNOOZE_KEY, String(Date.now()));
    setVisible(false);
  }, []);

  if (!visible) return null;

  const ios = isIOS();

  return (
    <div className="bg-primary/10 border-b border-primary/20 px-4 py-3">
      <div className="container mx-auto max-w-2xl space-y-2">
        <div className="flex items-start gap-3">
          <RefreshCw className="w-5 h-5 text-primary shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0 space-y-1.5">
            <p className="text-sm font-semibold text-foreground">
              Atualize seu app ForjaHub
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Fizemos melhorias visuais (novo ícone e mais). Para aplicar, reinstale o app:
            </p>

            {ios ? (
              <ol className="text-xs text-muted-foreground leading-relaxed space-y-1 list-none m-0 p-0">
                <li><span className="font-bold text-primary">1.</span> Delete o app ForjaHub da tela inicial.</li>
                <li><span className="font-bold text-primary">2.</span> Abra <strong className="text-foreground">forjahub.com</strong> no Safari.</li>
                <li><span className="font-bold text-primary">3.</span> Toque em <strong>Compartilhar</strong> (↑) → <strong>"Adicionar à Tela de Início"</strong>.</li>
              </ol>
            ) : (
              <ol className="text-xs text-muted-foreground leading-relaxed space-y-1 list-none m-0 p-0">
                <li><span className="font-bold text-primary">1.</span> Desinstale o app ForjaHub do seu dispositivo.</li>
                <li><span className="font-bold text-primary">2.</span> Abra <strong className="text-foreground">forjahub.com</strong> no Chrome.</li>
                <li><span className="font-bold text-primary">3.</span> Toque em <strong>⋮</strong> → <strong>"Instalar app"</strong> ou <strong>"Adicionar à tela inicial"</strong>.</li>
              </ol>
            )}
          </div>

          <button
            onClick={handleSnooze}
            className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
            aria-label="Fechar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex gap-2 justify-end">
          <Button variant="ghost" size="sm" className="text-xs h-8" onClick={handleSnooze}>
            Lembrar depois
          </Button>
          <Button size="sm" className="text-xs h-8" onClick={handleDone}>
            Já reinstalei ✓
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PWAReinstallBanner;
